/**
 * Auth-gated favorite completion.
 *
 * A signed-out visitor who taps "favorite" is sent to sign in and, on return,
 * the home is favorited automatically — the auth-gated action *completes*
 * (USER_STORIES acceptance criterion), mirroring the Save-search intent flow.
 *
 * The pending listing id survives the sign-in redirect in sessionStorage (same
 * tab, cleared on read). `AuthMenu` consumes it once auth resolves.
 */
const KEY = 'goreal:fav-intent';

/**
 * URL flag saying "this visitor arrived mid-favorite".
 *
 * sessionStorage is invisible to the server, so a sign-in page that reads only
 * sessionStorage must render the wrong greeting first and correct it after
 * hydration. This parameter is set at the same moment as the stored id and
 * makes the very first paint right.
 */
export const FAVORITE_INTENT_PARAM = 'intent';

export function setPendingFavorite(listingId: string): void {
  try {
    sessionStorage.setItem(KEY, listingId);
  } catch {
    // storage unavailable (private mode) — the sign-in still happens, the
    // favorite just won't auto-resume; no crash.
  }
}

/**
 * Read the pending favorite id WITHOUT clearing it.
 *
 * Mirrors `peekTourIntent`. The sign-in page needs to know *why* the visitor is
 * here in order to address them correctly — a guest sent here by tapping a
 * heart was being greeted "Welcome back. Log in to your saved homes, searches,
 * and tours.", which is addressed to somebody who has an account and saved
 * homes, i.e. not this person.
 *
 * Deliberately not `consumePendingFavorite`: `AuthMenu` is the one consumer,
 * and completing the favorite on return depends on the id still being there.
 */
export function peekPendingFavorite(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Read and clear the pending favorite id (one-shot). */
export function consumePendingFavorite(): string | null {
  try {
    const id = sessionStorage.getItem(KEY);
    if (id) sessionStorage.removeItem(KEY);
    return id;
  } catch {
    return null;
  }
}

/**
 * Send a signed-out visitor to sign in, remembering both the home they wanted
 * to favorite AND the exact filtered URL to return to (pathname + search — so
 * the search context isn't lost on the way back).
 */
export function startGuestFavorite(listingId: string): void {
  setPendingFavorite(listingId);
  const back = window.location.pathname + window.location.search;
  // `intent=save` duplicates what sessionStorage already holds, on purpose: it
  // is the half the SERVER can see. Without it the sign-in page renders
  // "Welcome back." into the HTML and only corrects itself after hydration —
  // a visible flash of exactly the wrong greeting. sessionStorage stays the
  // authority for whether a favorite is really pending; the parameter is just
  // what lets the first paint be right.
  window.location.href = `/login?redirect=${encodeURIComponent(back)}&${FAVORITE_INTENT_PARAM}=save`;
}
